import type { UserState } from '@/hooks/use-user-state';
import { getCachedAgentSlug } from '@/utils/agent-cache';

export interface RouteParams {
  org?: string;
  agent?: string;
}

export interface FacilityResolution {
  organizationId: string | undefined;
  facilityId: string | undefined;
  agentId: string | undefined;
}

/**
 * Resolve an organization ID from router params or cached facility context.
 *
 * Priority: route `org` slug → cached org slug → facility cache mapped back to its org →
 * first known organization. Returns `undefined` until user organizations are available.
 *
 * @param params - Route params containing org and agent slugs.
 * @param userState - User-scoped organization/facility state; may be undefined while loading.
 * @returns The matching organization ID, the first organization ID, or undefined when none apply.
 */
export function resolveOrganizationId(
  params: RouteParams,
  userState: UserState | undefined
): string | undefined {
  const cached = getCachedAgentSlug();

  if (!userState?.organizations) {
    return undefined;
  }

  const orgFromParams = params.org ? userState.organizations.urlToId[params.org] : undefined;
  if (orgFromParams) {
    return orgFromParams;
  }

  if (cached?.orgName) {
    const orgFromCache = userState.organizations.urlToId[cached.orgName];
    if (orgFromCache) {
      return orgFromCache;
    }
  }

  if (cached?.shortname && userState.facilities) {
    const facilityId = userState.facilities.urlToId[cached.shortname];
    const facilityOrg = facilityId
      ? userState.facilities.byId[facilityId]?.organizationId
      : undefined;

    if (facilityOrg && userState.organizations.byId[facilityOrg]) {
      return facilityOrg;
    }
  }

  return userState.organizations.unordered[0]?.organizationId;
}

/**
 * Resolve a facility ID for a given organization using params or cached slug.
 *
 * Priority: route `agent` slug (if it belongs to the organization) → cached facility slug
 * (if it belongs to the organization) → first facility listed for the organization.
 * Returns `undefined` when the organization is unknown or facilities are unavailable.
 *
 * @param params - Route params containing the facility/agent slug.
 * @param organizationId - Organization context used to validate facility ownership.
 * @param userState - User-scoped organization/facility state; may be undefined while loading.
 * @returns The resolved facility ID scoped to the organization, or undefined when not found.
 */
export function resolveFacilityId(
  params: RouteParams,
  organizationId: string | undefined,
  userState: UserState | undefined
): string | undefined {
  const cached = getCachedAgentSlug();

  if (!userState?.facilities || !organizationId) {
    return undefined;
  }

  const currentOrganization = userState.organizations?.byId[organizationId];
  if (!currentOrganization) {
    return undefined;
  }

  const orgFacilities = currentOrganization.facilities || [];

  if (params.agent) {
    const facilityFromParam = userState.facilities.urlToId[params.agent];
    if (facilityFromParam) {
      const facility = userState.facilities.byId[facilityFromParam];
      if (facility && facility.organizationId === organizationId) {
        return facilityFromParam;
      }
    }
  }

  if (cached?.shortname) {
    const facilityFromCache = userState.facilities.urlToId[cached.shortname];
    if (facilityFromCache) {
      const facility = userState.facilities.byId[facilityFromCache];
      if (facility && facility.organizationId === organizationId) {
        return facilityFromCache;
      }
    }
  }

  return orgFacilities[0]?.facilityId;
}

/**
 * Resolve an agent ID for a facility by selecting the first known agent.
 *
 * @param facilityId - Facility context used to look up agents.
 * @param userState - User-scoped organization/facility state; may be undefined while loading.
 * @returns The first agent ID for the facility, or undefined when missing.
 */
export function resolveAgentId(
  facilityId: string | undefined,
  userState: UserState | undefined
): string | undefined {
  if (!facilityId || !userState?.facilities) {
    return undefined;
  }

  const facility = userState.facilities.byId[facilityId];
  return facility?.agents[0]?.agentId;
}

/**
 * Convenience wrapper that resolves the organization, facility, and agent IDs using
 * the shared fallback order across helper functions.
 *
 * @param params - Route params containing org and agent slugs.
 * @param userState - User-scoped organization/facility state; may be undefined while loading.
 * @returns A resolution tuple containing the organization, facility, and agent IDs.
 */
export function resolveFacilityFromParams(
  params: RouteParams,
  userState: UserState | undefined
): FacilityResolution {
  const organizationId = resolveOrganizationId(params, userState);
  const facilityId = resolveFacilityId(params, organizationId, userState);
  const agentId = resolveAgentId(facilityId, userState);

  return {
    organizationId,
    facilityId,
    agentId,
  };
}
